import { ApolloProvider, useApolloClient } from '@apollo/react-hooks'
import type { OfflineQuotesQuery, AppDocsQuery } from '@paintscout/api'
import {
  AppDocsDocument,
  OfflineQuotesDocument,
  updateCachedContact,
  updateCachedQuote,
  useAppDocsQuery
} from '@paintscout/api'
import { getHighestRole } from '@paintscout/util/users'
import * as Sentry from '@sentry/react'
import { createLogger } from '@paintscout/logger'
import { SnackbarProvider, TranslationProvider } from '@paintscout/ui'
import { Theme, UserProvider } from '@paintscout/ui-business'
import type ApolloCache from '@wora/apollo-cache'
import type ApolloClient from 'apollo-client'
import { error as analyticsError } from 'shared/util/analytics'
import type { QuoteDocument } from 'paintscout'
import React, { useEffect, useState } from 'react'
import { Authorizer, useAuth } from 'react-auth0'
import { useConnection } from 'react-detect-offline'
import { useHistory } from 'react-router-dom'
import { ApolloQueue, createApolloClient } from 'shared/graphql'
import AppLoading from 'src/components/App/AppLoading'
import { getAuthToken } from 'shared/graphql/client'
import LaunchDarkly from 'shared/components/common/LaunchDarkly'
import ClientOptionsProvider from './ClientOptionsProvider'
import { buildUserForProvider } from './buildUserForProvider'

const log = createLogger('authorize-user')

const UNEXPECTED_ERROR_MESSAGE = 'An unexpected error occurred'

export default function AuthorizeUser({ children }: { children?: React.ReactNode }) {
  const { session } = useAuth()

  const history = useHistory()
  const isSuperadmin = session?.app_metadata?.roles?.includes('superadmin')
  const isSupport = session?.app_metadata?.roles?.includes('support')
  const isCorporate = session?.app_metadata?.roles?.includes('corporate')
  const [client, setClient] = useState<ApolloClient<any>>(null)
  const urlSearchParams = new URLSearchParams(history.location.search)
  function getMasqueradeCompanyId() {
    let res = null
    if (isSuperadmin || isSupport || isCorporate) {
      const existingStorageId = window?.localStorage?.getItem('masqueradeCompanyId')
      if (existingStorageId && existingStorageId !== 'null') {
        res = existingStorageId
      } else {
        const searchParamId = urlSearchParams.get('masqueradeCompanyId')
        if (searchParamId) {
          res = searchParamId
          window.localStorage.setItem('masqueradeCompanyId', searchParamId)
        }
      }
    }

    return res
  }
  const [masqueradeCompanyId, setMasqueradeCompanyId] = useState(getMasqueradeCompanyId())

  useEffect(() => {
    setMasqueradeCompanyId(getMasqueradeCompanyId())
  }, [history.location.pathname])

  useEffect(() => {
    async function supportsIdb() {
      await new Promise((resolve, reject) => {
        const db = window.indexedDB.open('test')

        db.onerror = reject

        db.onsuccess = () => {
          window.indexedDB.deleteDatabase('test')
          resolve(null)
        }
      })
    }

    async function syncLastLoginToHubSpot(userId: string) {
      try {
        const response = await fetch('/api/hubspot/sync-last-login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            lastLogin: new Date().toISOString()
          })
        })

        if (!response.ok) {
          console.error('Failed to sync last login to HubSpot:', response.statusText)
        }
      } catch (error) {
        console.error('Error syncing last login to HubSpot:', error)
      }
    }

    async function init() {
      if (session) {
        let enableOffline = false
        try {
          await supportsIdb() // check if idb is supported on this browser
          enableOffline = true
        } catch (e) {
          console.log(e)
          window.alert(
            'Your browser is unsupported or running in incognito/private mode. If you go offline, you will be unable to save your changes.'
          )
        }

        try {
          const client = createApolloClient({
            idbName: session.user_id,
            enableOffline
          })

          await client.hydrate()
          await new Promise((resolve) => setTimeout(resolve))
          setClient(client)

          // Sync last login to HubSpot
          await syncLastLoginToHubSpot(session.user_id)

          try {
            await getAuthToken()
          } catch (err) {
            if (
              err?.message?.includes('invalid_grant') ||
              err?.includes('invalid_grant') ||
              err?.error?.includes('invalid_grant')
            ) {
              if (localStorage && !localStorage.getItem('reloaded')) {
                localStorage.setItem('reloaded', 'true')
                window.location.reload()
              } else {
                localStorage.removeItem('reloaded')
                Sentry.captureException(err, { tags: { message: 'Error with refresh token' } })
                history.push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
              }
            }
          }
        } catch (error) {
          console.log('Unable to authorize user', error)
          Sentry.captureException(error)
          history.push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
        }
      }
    }

    init()
    // re-init client if session.user_id changes
  }, [session?.user_id, masqueradeCompanyId]) // eslint-disable-line

  // Listen for stoage events across tabs, keep out masqueradeCompanyId in sync across tabs
  window.addEventListener('storage', (event) => {
    if (event.key === 'masqueradeCompanyId') {
      setMasqueradeCompanyId(event.newValue)
      window.localStorage.removeItem('lastPurge')
      history.push('/')
    }
  })

  return (
    <Authorizer>
      {!client ? (
        <AppLoading />
      ) : (
        // key by user_id so that we completely remount the apollo client if it happens to change
        // (this *shouldn't* happen... but it's an easy failsafe)
        <ApolloProvider key={session?.user_id} client={client}>
          <ApolloQueue>
            <LoadData>{children}</LoadData>
          </ApolloQueue>
        </ApolloProvider>
      )}
    </Authorizer>
  )
}

// fetch necessary data and put in appropriate providers
const LoadData = ({ children }: { children?: React.ReactNode }) => {
  const history = useHistory()
  const { session } = useAuth()
  const apolloClient = useApolloClient()
  const { online } = useConnection()

  const [launchDarklyReady, setLaunchDarklyReady] = useState(false)
  const [purging, setPurging] = useState(() => online && checkNeedsPurge())

  function handleUnexpectedError() {
    history.push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
  }

  useEffect(() => {
    Sentry.setUser({
      username: session.name,
      email: session.email,
      id: session.user_id
    })

    const role = getHighestRole(session.app_metadata.roles)
    Sentry.setTags({
      role,
      company: session.app_metadata.company?.name,
      companyId: session.app_metadata.companyId
    })
  }, [session])

  useEffect(() => {
    if (purging) {
      purge()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purging])

  async function purge() {
    try {
      const appDocs = apolloClient.readQuery({
        query: AppDocsDocument
      })
      const offlineQuotes = apolloClient.readQuery<OfflineQuotesQuery>({
        query: OfflineQuotesDocument
      })

      await apolloClient.cache.reset()

      window.localStorage.setItem('lastPurge', JSON.stringify(Date.now()))

      apolloClient.writeQuery({
        query: AppDocsDocument,
        data: appDocs
      })

      updateCache(offlineQuotes)
    } catch (error) {
      // if lastPurge is undefined, then this is expected because it's probably a brand new database
      if (JSON.parse(window.localStorage.getItem('lastPurge'))) {
        log.error('Unable to purge cache', { error: error?.error ?? error, sentry: true })
      }
    }
    setPurging(false)
  }

  /**
   * updates various cached queries using results of offlineQuotes
   */
  function updateCache(offlineQuotes: OfflineQuotesQuery) {
    if (offlineQuotes) {
      // put appDocs back into cache
      apolloClient.writeQuery({
        query: OfflineQuotesDocument,
        data: offlineQuotes
      })

      // get response of every offline quote and update the quote caches
      const { quotes = [], invoices = [], contacts = [], templates = [] } = offlineQuotes.offlineQuotes ?? {}

      const allQuotes = [...quotes, ...invoices, ...templates]
      allQuotes.forEach((quote) => {
        updateCachedQuote(apolloClient.cache as ApolloCache, quote)
      })

      contacts.forEach((contact) => {
        updateCachedContact(apolloClient.cache as ApolloCache, contact as any)
      })
    }
  }

  const appDocsQuery = useAppDocsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    onCompleted: async () => {
      const unsavedNewQuotes: QuoteDocument[] = []

      // we're online - try fetching offline quotes
      const offlineQuotesResponse = await apolloClient
        .query<OfflineQuotesQuery>({
          query: OfflineQuotesDocument,
          // network-only will still write to cache
          fetchPolicy: 'network-only'
        })
        .catch(() => null)

      if (offlineQuotesResponse) {
        updateCache(offlineQuotesResponse.data)

        const all = [
          ...(offlineQuotesResponse.data?.offlineQuotes?.quotes ?? []),
          ...(offlineQuotesResponse.data?.offlineQuotes?.invoices ?? [])
        ]

        // temporary check to make sure all new offline quotes made it
        // (this could be a false positive if 10 new offline quotes come in and push it beyond the limit)
        unsavedNewQuotes.forEach((quote) => {
          if (!all.find((q) => q._id === quote._id)) {
            console.error('Potentially lost offline quote', quote)
            analyticsError('Pontentially lost offline quote', quote)
          }
        })
      }
    }
  })

  useEffect(() => {
    if (appDocsQuery.error && !appDocsQuery.data?.appDocs) {
      Sentry.captureException(appDocsQuery.error, { tags: { message: 'Unexpected error in AuthorizeUser' } })
      handleUnexpectedError()
    }
  }, [appDocsQuery.error])

  if (!appDocsQuery?.data?.appDocs || purging) {
    return <AppLoading />
  }

  const { preferences, companyDetails } = appDocsQuery.data.appDocs
  const companyId = window?.localStorage?.getItem('masqueradeCompanyId') || session.app_metadata.companyId

  if (!companyDetails) {
    Sentry.captureMessage('AuthorizeUser: appDocs returned without companyDetails', {
      level: 'warning',
      tags: { masquerading: String(!!window?.localStorage?.getItem('masqueradeCompanyId')) },
      extra: { companyId }
    })
  }

  return (
    <ClientOptionsProvider clientId={companyId}>
      <UserProvider
        user={buildUserForProvider({ session, companyDetails })}
        preferences={preferences}
        remainingQuotes={appDocsQuery.data.appDocs.remainingQuotes}
        togglePaymentExpiryNoticeCache={() => {
          apolloClient.cache.writeQuery<AppDocsQuery>({
            query: AppDocsDocument,
            data: {
              ...appDocsQuery.data,
              appDocs: {
                ...appDocsQuery.data.appDocs,
                companyDetails: { ...appDocsQuery.data.appDocs.companyDetails, paymentExpiryNotice: false }
              }
            }
          })
        }}
      >
        {/*
          lang is intentionally hardcoded to 'en' because the internal app still has no per-user language preference.
          If that changes, swap this to a real preference value and move the provider lower only if runtime language
          changes need to be scoped to smaller translated subtrees.
        */}
        <TranslationProvider lang="en">
          <LaunchDarkly
            onReady={() => setLaunchDarklyReady(true)}
            userDetails={{
              id: session.user_id,
              companyId,
              parentCompanyId: companyDetails?.parentCompanyId || null,
              email: session.email
            }}
          >
            <Theme>
              <SnackbarProvider>{launchDarklyReady || !online ? children : <AppLoading />}</SnackbarProvider>
            </Theme>
          </LaunchDarkly>
        </TranslationProvider>
      </UserProvider>
    </ClientOptionsProvider>
  )
}

const checkNeedsPurge = () => {
  const lastPurge = JSON.parse(window.localStorage.getItem('lastPurge')) || 0
  const needsPurge = lastPurge < Date.now() - 1000 * 60 * 60 * 24 // once a day

  return needsPurge
}
